import type { NavigationGuardNext, RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import Bugsnag from '@bugsnag/js'

import {
  AUTO_ATTEMPT_KEY,
  getAccessToken,
  handleAuthCallback,
  hasValidAuthContext,
  initiateAuthorize,
  INTERNAL_REDIRECT_KEY,
  restoreSession,
} from '@/api/idp'
import InvalidPortalError from '@/errors/InvalidPortalError'
import AppService from '@/services/appService'
import { useAppStore } from '@/stores/app'
import { isEmbedded } from '@/api/client'

const MAX_AUTO_ATTEMPTS = 2

export function normalizeRedirect(path?: string | null): string | null {
  if (!path || path === '/' || path === '/login') {
    return null
  }
  if (path.startsWith('/login?') || path.startsWith('/login/')) {
    return null
  }
  // Wizard routes requiring in-memory state: redirect to /mode for that business profile
  const wizardMatch = path.match(
    /^\/app\/(\d+)\/(editor|validate|download|generate|convert)(\b|\/|\?)/i,
  )
  if (wizardMatch) {
    return `/app/${wizardMatch[1]}/mode`
  }
  if (/^\/(editor|validate|download|generate|convert)(\b|\/|\?)/i.test(path)) {
    return null
  }
  return path
}

export const AUTO_LOGIN_QUERY = 'auto'
export const AUTO_LOGIN_STORAGE_KEY = 'dx_login_auto'

export function loginLocation(options?: {
  redirect?: string
  auto?: boolean
}): RouteLocationRaw {
  if (options?.auto) {
    sessionStorage.setItem(AUTO_LOGIN_STORAGE_KEY, 'pending')
  }

  const query: Record<string, string> = {}
  if (options?.redirect) {
    query.redirect = options.redirect
  }
  if (options?.auto) {
    query[AUTO_LOGIN_QUERY] = '1'
  }

  return { name: 'Login', query }
}

export function shouldAutoSignIn(autoQuery: unknown): boolean {
  const pending = sessionStorage.getItem(AUTO_LOGIN_STORAGE_KEY) === 'pending'
  if (autoQuery === '1' && pending) {
    sessionStorage.setItem(AUTO_LOGIN_STORAGE_KEY, 'started')
    return true
  }

  sessionStorage.removeItem(AUTO_LOGIN_STORAGE_KEY)
  return false
}

export async function resolvePostLoginRoute(
  to: RouteLocationNormalized,
  options?: { redirectPath?: string | null }
): Promise<RouteLocationRaw> {
  const appStore = useAppStore()
  const appService = new AppService()

  if (!appStore.user) {
    await appStore.hydrateUserFromToken()
  }

  if (appStore.portal == null) {
    const portalDomain = import.meta.env.DEV ? 'be.stunnr.co' : window.location.hostname
    try {
      appStore.setPortal(await appService.getPortalData(portalDomain))
    } catch (error) {
      console.error(error)
    }
  }

  if (appStore.businessProfiles.length === 0 && appStore.user && appStore.portal) {
    try {
      appStore.setBusinessProfiles(
        await appService.getPortalBusinessProfiles(appStore.user.id, appStore.portal.id),
      )
    } catch (error) {
      console.error(error)
    }
  }

  const explicitRedirect = normalizeRedirect(
    options?.redirectPath ||
    (typeof to.query.redirect === 'string' ? to.query.redirect : null) ||
    sessionStorage.getItem(INTERNAL_REDIRECT_KEY)
  )
  sessionStorage.removeItem(INTERNAL_REDIRECT_KEY)

  if (explicitRedirect) {
    sessionStorage.setItem('dx_post_login_redirect', explicitRedirect)
    return explicitRedirect
  }

  if (appStore.businessProfiles.length === 0) {
    return { name: 'ProfileSelection' }
  }

  if (appStore.businessProfiles.length === 1) {
    const businessProfileId = appStore.businessProfiles[0].id
    appStore.setSelectedBusinessProfileId(businessProfileId)
    return { name: 'Mode', params: { businessProfileId } }
  }

  return { name: 'ProfileSelection' }
}

export async function completeLoginWithBusinessProfile(
  businessProfileId: number,
  redirect?: string | null,
): Promise<RouteLocationRaw> {
  const appStore = useAppStore()
  appStore.setSelectedBusinessProfileId(businessProfileId)

  const normalizedRedirect = normalizeRedirect(redirect ?? null)
  if (normalizedRedirect) {
    sessionStorage.setItem('dx_post_login_redirect', normalizedRedirect)
    return normalizedRedirect
  }

  return { name: 'Mode', params: { businessProfileId } }
}

export async function loginGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  await restoreSession()

  const accessToken = getAccessToken()
  if (accessToken) {
    const destination = await resolvePostLoginRoute(to)
    next(destination)
    return
  }

  if (isEmbedded) {
    next(false)
    return
  }

  const redirectTarget =
    normalizeRedirect(typeof to.query.redirect === 'string' ? to.query.redirect : null) ||
    normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY)) ||
    '/'
  sessionStorage.setItem(INTERNAL_REDIRECT_KEY, redirectTarget)

  next()
}

export async function processAuthCallback(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | null> {
  const error = typeof to.query.error === 'string' ? to.query.error : null

  if (error) {
    sessionStorage.removeItem(AUTO_ATTEMPT_KEY)
    const savedRedirect = normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY))
    return {
      name: 'Login',
      query: savedRedirect ? { redirect: savedRedirect } : {},
    }
  }

  const code = typeof to.query.code === 'string' ? to.query.code : null
  const state = typeof to.query.state === 'string' ? to.query.state : null

  if (code && state && hasValidAuthContext(state)) {
    let redirect: string | null = null
    let authSuccess = false
    try {
      redirect = await handleAuthCallback(code, state)
      authSuccess = true
    } catch (callbackError) {
      console.error(callbackError)
      Bugsnag.notify(callbackError instanceof Error ? callbackError : new Error(String(callbackError)))
    }

    if (authSuccess) {
      sessionStorage.removeItem(AUTO_ATTEMPT_KEY)

      const appStore = useAppStore()
      try {
        await appStore.hydrateUserFromToken()
        const destination = await resolvePostLoginRoute(to, { redirectPath: redirect })
        return destination
      } catch (hydrateError) {
        console.error('Failed to hydrate user:', hydrateError)
        return null
      }
    }
  }

  const attempts = parseInt(sessionStorage.getItem(AUTO_ATTEMPT_KEY) || '0', 10)
  if (attempts < MAX_AUTO_ATTEMPTS) {
    sessionStorage.setItem(AUTO_ATTEMPT_KEY, String(attempts + 1))
    const redirectTarget = normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY)) || '/'
    await initiateAuthorize(redirectTarget, 'none')
    return null
  }

  const savedRedirect = normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY))
  sessionStorage.removeItem(AUTO_ATTEMPT_KEY)
  return {
    name: 'Login',
    query: savedRedirect ? { redirect: savedRedirect } : {},
  }
}

export async function authCallbackGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const destination = await processAuthCallback(to)
  if (destination) {
    next(destination)
  } else {
    next(false)
  }
}

export function isEmbedRoute(to: RouteLocationNormalized): boolean {
  return typeof to.query.token === 'string' && to.query.token.length > 0
}

export async function globalRouterGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const appStore = useAppStore()
  const appService = new AppService()

  if (isEmbedRoute(to)) {
    next()
    return
  }

  await restoreSession()

  let accessToken = getAccessToken()
  let user = appStore.user

  if (accessToken && !user) {
    try {
      user = await appStore.hydrateUserFromToken()
    } catch (e) {
      console.error('Failed to hydrate user in globalRouterGuard:', e)
    }
  }

  if (to.name === 'Forbidden' || to.name === 'NotFound') {
    next()
    return
  }

  if (appStore.portal == null) {
    const portalDomain = import.meta.env.DEV ? 'be.stunnr.co' : window.location.hostname
    try {
      appStore.setPortal(await appService.getPortalData(portalDomain))
    } catch (error) {
      console.error(error)
      if (error instanceof InvalidPortalError) {
        next({ name: 'NotFound' })
        return
      }
    }
  }

  if (appStore.businessProfiles.length === 0 && appStore.user && appStore.portal) {
    appStore.setBusinessProfiles(
      await appService.getPortalBusinessProfiles(appStore.user.id, appStore.portal.id),
    )
  }

  if (to.meta.requiresAuth) {
    accessToken = getAccessToken()
    if (!user || !accessToken) {
      if (isEmbedded) {
        next(false)
        return
      }
      next(loginLocation({ redirect: to.fullPath, auto: true }))
      return
    }
  }

  if (to.name === 'Default' && !user && !accessToken) {
    next(loginLocation({ redirect: to.fullPath, auto: true }))
    return
  }

  if (to.name === 'Default' && user && accessToken) {
    next(await resolvePostLoginRoute(to))
    return
  }

  if (sessionStorage.getItem('dx_post_login_redirect')) {
    sessionStorage.removeItem('dx_post_login_redirect')
  }

  // In stunnr-be, all wizard steps beyond Mode require in-memory PSD/products/canvas state.
  // On cold visit or after auth transition (Callback/Login), in-memory state is wiped,
  // so these must safely restart from Mode.
  const protectedRouteNames = ['Editor', 'Validate', 'Download', 'Generate', 'Convert']
  const authOrColdSources = ['Default', 'Callback', 'Login']
  if (
    protectedRouteNames.includes(to.name as string) &&
    (!from.name ||
      authOrColdSources.includes(from.name as string) ||
      from.path === '/' ||
      from.path === '/callback' ||
      from.path === '/login')
  ) {
    const businessProfileId =
      to.params.businessProfileId ||
      appStore.selectedBusinessProfileId ||
      appStore.businessProfiles[0]?.id
    if (businessProfileId) {
      next({
        name: 'Mode',
        params: { businessProfileId },
      })
      return
    }
  }

  next()
}
